import {
  CTCSS_TONES,
  DTCS_CODES,
  compareImages,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const BLOCK_SIZE = 64
const BLOCK_COUNT = 448
const MEMORY_SIZE = 0x6fc9
const CHECKSUM_OFFSET = 0x6fc8
const CHANNEL_OFFSET = 0x0248
const CHANNEL_SIZE = 16
const NAME_OFFSET = 0x4708
const NAME_SIZE = 8
const SKIP_OFFSET = 0x6ec8
const IDENTITY = 'AH017'
const CHARSET =
  "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ !`o$%&'()*+,-./|;/=>?@[u]^_"

export type FT60Profile = {
  protocol: 'ft60'
  id: string
  vendor: string
  model: string
  chirpModel: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 1
  channelMaximum: 1000
  nameLength: 6
  powerLevels: readonly ['High', 'Mid', 'Low']
  memorySize: number
}

export const FT60_PROFILES: readonly FT60Profile[] = [
  {
    protocol: 'ft60',
    id: 'yaesu-ft-60',
    vendor: 'Yaesu',
    model: 'FT-60 / FT-60R',
    chirpModel: 'FT-60',
    summary:
      'Yaesu FT-60 and FT-60R handhelds using the AH017 clone protocol.',
    tags: [
      'Experimental',
      'Yaesu AH017',
      'Read + write',
      '1,000 channels',
      'AM / FM',
    ],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: 1000,
    nameLength: 6,
    powerLevels: ['High', 'Mid', 'Low'],
    memorySize: MEMORY_SIZE,
  },
]

function emptyChannel(
  number: number,
  profile: FT60Profile,
): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'FM',
    power: profile.powerLevels[0],
    skip: false,
  }
}

function decodePackedBcd(raw: Uint8Array): number {
  let value = 0
  for (const byte of raw) {
    value = value * 100 + (byte >> 4) * 10 + (byte & 0x0f)
  }
  return value
}

function requiredStep(frequency: number): number {
  if (frequency % 5000 === 0) return 5000
  if (frequency % 10000 === 0) return 10000
  if (frequency % 12500 === 0) return 12500
  if (frequency % 6250 === 0) return 6250
  if (frequency % 2500 === 0) return 2500
  if (frequency % 1000 === 0) return 1000
  if (frequency % 500 === 0) return 500
  if ((frequency % 25000 === 8330 || frequency % 25000 === 8333) ||
      (frequency % 25000 === 16660 || frequency % 25000 === 16666)) {
    return 8330
  }
  return 250
}

export function decodeFT60Frequency(raw: Uint8Array): number | null {
  if (raw.byteLength !== 3 || raw.every((value) => value === 0xff)) return null
  let frequency = decodePackedBcd(raw) * 10000
  if (frequency > 8_000_000_000) {
    frequency = frequency - 8_000_000_000 + 5000
  }
  if (frequency > 4_000_000_000) {
    frequency -= 4_000_000_000
    for (let attempt = 0; attempt < 3; attempt += 1) {
      frequency += 2500
      if (requiredStep(frequency) === 12500) break
    }
  }
  return frequency
}

export function encodeFT60Frequency(frequency: number): Uint8Array {
  if (!Number.isFinite(frequency) || frequency <= 0) {
    throw new Error('A valid frequency is required.')
  }
  const raw = new Uint8Array(3)
  let value = Math.floor(frequency / 10000)
  for (let index = raw.byteLength - 1; index >= 0; index -= 1) {
    const part = value % 100
    raw[index] = ((Math.floor(part / 10) << 4) | (part % 10)) & 0xff
    value = Math.floor(value / 100)
  }
  if (value !== 0) {
    throw new Error(
      `${(frequency / 1_000_000).toFixed(6)} MHz cannot be represented by the FT-60.`,
    )
  }
  let flags = 0
  if ((frequency / 1000) % 10 >= 5) flags |= 0x80
  if (
    frequency % 5000 !== 0 &&
    (frequency % 12500 === 0 || frequency % 6250 === 0)
  ) {
    flags |= 0x40
  }
  raw[0] |= flags
  return raw
}

function decodeName(
  image: Uint8Array,
  profile: FT60Profile,
  number: number,
): string {
  const offset = NAME_OFFSET + (number - profile.channelMinimum) * NAME_SIZE
  if (
    (image[offset + 6] & 0x80) === 0 ||
    (image[offset + 7] & 0x80) === 0
  ) {
    return ''
  }
  let name = ''
  for (let index = 0; index < profile.nameLength; index += 1) {
    const code = image[offset + index]
    if (code === 0xff) break
    name += CHARSET[code] ?? ' '
  }
  return name.trimEnd()
}

function encodeName(
  image: Uint8Array,
  profile: FT60Profile,
  number: number,
  name: string,
): void {
  const offset = NAME_OFFSET + (number - profile.channelMinimum) * NAME_SIZE
  const normalized = name.trim().toUpperCase().slice(0, profile.nameLength)
  for (let index = 0; index < profile.nameLength; index += 1) {
    const character = normalized[index] ?? ' '
    image[offset + index] = Math.max(0, CHARSET.indexOf(character))
  }
  image[offset + 6] =
    (image[offset + 6] & 0x7f) | (normalized ? 0x80 : 0)
  image[offset + 7] =
    (image[offset + 7] & 0x7f) | (normalized ? 0x80 : 0)
}

function decodeTone(
  raw: Uint8Array,
): Pick<WebChannel, 'toneMode' | 'ctcss' | 'dtcs' | 'dtcsPolarity'> {
  const mode = raw[4] & 0x07
  const tone = raw[8] & 0x3f
  const dtcs = raw[9] & 0x7f
  if (mode === 1 || mode === 2 || mode === 3 || mode === 6) {
    return {
      toneMode: 'ctcss',
      ctcss: CTCSS_TONES[tone] ?? CTCSS_TONES[0],
      dtcs: null,
      dtcsPolarity: 'N',
    }
  }
  if (mode === 4 || mode === 5 || mode === 7) {
    return {
      toneMode: 'dtcs',
      ctcss: null,
      dtcs: DTCS_CODES[dtcs] ?? DTCS_CODES[0],
      dtcsPolarity: 'N',
    }
  }
  return { toneMode: 'none', ctcss: null, dtcs: null, dtcsPolarity: 'N' }
}

function offsetForChannel(raw: Uint8Array): number {
  return (((raw[11] & 0x7f) << 8) | raw[12]) * 50_000
}

function txFrequencyForChannel(
  raw: Uint8Array,
  rxFrequency: number,
  duplex: number,
): number | null {
  if (duplex === 2) return rxFrequency - offsetForChannel(raw)
  if (duplex === 3) return rxFrequency + offsetForChannel(raw)
  if (duplex === 4) return decodeFT60Frequency(raw.slice(5, 8))
  if (duplex === 5) return null
  return rxFrequency
}

export function parseFT60Channel(
  image: Uint8Array,
  profile: FT60Profile,
  number: number,
): WebChannel {
  if (number < profile.channelMinimum || number > profile.channelMaximum) {
    throw new Error(`Channel ${number} is outside the FT-60 range.`)
  }
  const offset =
    CHANNEL_OFFSET + (number - profile.channelMinimum) * CHANNEL_SIZE
  const raw = image.slice(offset, offset + CHANNEL_SIZE)
  if (raw.byteLength !== CHANNEL_SIZE || (raw[0] & 0x80) === 0) {
    return emptyChannel(number, profile)
  }
  const rxFrequency = decodeFT60Frequency(raw.slice(1, 4))
  if (!rxFrequency) return emptyChannel(number, profile)
  const duplex = raw[0] & 0x0f
  const powerIndex = (raw[8] >> 6) & 0x03
  const skipIndex =
    (image[SKIP_OFFSET + ((number - 1) >> 2)] >>
      (((number - 1) & 3) * 2)) &
    0x03
  return {
    number,
    empty: false,
    name: decodeName(image, profile, number),
    rxFrequency,
    txFrequency: txFrequencyForChannel(raw, rxFrequency, duplex),
    ...decodeTone(raw),
    mode:
      (raw[0] & 0x10) !== 0
        ? 'AM'
        : (raw[0] & 0x20) !== 0
          ? 'NFM'
          : 'FM',
    power: profile.powerLevels[powerIndex] ?? profile.powerLevels[0],
    skip: skipIndex !== 0,
  }
}

export function parseFT60Channels(
  image: Uint8Array,
  profile: FT60Profile,
): WebChannel[] {
  return Array.from(
    { length: profile.channelMaximum },
    (_, index) => parseFT60Channel(image, profile, index + profile.channelMinimum),
  )
}

function writeShift(raw: Uint8Array, magnitudeUnits: number): void {
  const units = Math.round(magnitudeUnits)
  if (units < 0 || units > 0x7fff) {
    throw new Error('This repeater shift cannot be represented by the FT-60.')
  }
  raw[11] = (raw[11] & 0x80) | ((units >> 8) & 0x7f)
  raw[12] = units & 0xff
}

function encodeTone(raw: Uint8Array, channel: WebChannel): void {
  const previousMode = raw[4] & 0x07
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    const index = CTCSS_TONES.indexOf(
      channel.ctcss as (typeof CTCSS_TONES)[number],
    )
    if (index < 0) throw new Error('Unsupported CTCSS tone for the FT-60.')
    raw[4] = (raw[4] & 0xf8) | ([1, 2, 3, 6].includes(previousMode) ? previousMode : 1)
    raw[8] = (raw[8] & 0xc0) | index
    raw[9] &= 0x80
  } else if (channel.toneMode === 'dtcs' && channel.dtcs) {
    const index = DTCS_CODES.indexOf(channel.dtcs as (typeof DTCS_CODES)[number])
    if (index < 0) throw new Error('Unsupported DTCS code for the FT-60.')
    raw[4] = (raw[4] & 0xf8) | ([4, 5, 7].includes(previousMode) ? previousMode : 4)
    raw[9] = (raw[9] & 0x80) | index
    raw[8] &= 0xc0
  } else {
    raw[4] &= 0xf8
    raw[8] &= 0xc0
    raw[9] &= 0x80
  }
}

export function updateFT60Checksum(image: Uint8Array): void {
  if (image.byteLength < MEMORY_SIZE) {
    throw new Error(`The FT-60 image must contain ${MEMORY_SIZE} bytes.`)
  }
  let checksum = 0
  for (let index = 0; index < CHECKSUM_OFFSET; index += 1) {
    checksum = (checksum + image[index]) & 0xff
  }
  image[CHECKSUM_OFFSET] = checksum
}

export function applyFT60Channel(
  image: Uint8Array,
  profile: FT60Profile,
  channel: WebChannel,
): void {
  const offset =
    CHANNEL_OFFSET + (channel.number - profile.channelMinimum) * CHANNEL_SIZE
  const raw = image.subarray(offset, offset + CHANNEL_SIZE)
  if (channel.empty) {
    raw[0] &= 0x7f
    updateFT60Checksum(image)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')

  if ((raw[0] & 0x80) === 0) {
    const previousRaw = raw.slice()
    raw.fill(0)
    raw.set(previousRaw.slice(13, 16), 13)
  }
  raw[0] =
    (raw[0] & 0x30) |
    0x80 |
    (channel.mode === 'AM' ? 0x10 : channel.mode === 'NFM' ? 0x20 : 0)
  raw.set(encodeFT60Frequency(channel.rxFrequency), 1)

  const txFrequency = channel.txFrequency
  if (txFrequency === null) {
    raw[0] = (raw[0] & 0xf0) | 5
    raw.fill(0, 5, 8)
    writeShift(raw, 0)
  } else if (Math.abs(txFrequency - channel.rxFrequency) === 0) {
    raw[0] &= 0xf0
    raw.fill(0, 5, 8)
    writeShift(raw, 0)
  } else {
    const delta = txFrequency - channel.rxFrequency
    const shiftUnits = Math.abs(delta) / 50_000
    const preserveSplit = (raw[0] & 0x0f) === 4
    if (
      !preserveSplit &&
      Number.isInteger(shiftUnits) &&
      shiftUnits >= 1 &&
      shiftUnits <= 0x7fff
    ) {
      raw[0] = (raw[0] & 0xf0) | (delta < 0 ? 2 : 3)
      raw.fill(0, 5, 8)
      writeShift(raw, shiftUnits)
    } else {
      raw[0] = (raw[0] & 0xf0) | 4
      raw.set(encodeFT60Frequency(txFrequency), 5)
      writeShift(raw, 0)
    }
  }

  encodeTone(raw, channel)
  const powerIndex = Math.max(
    0,
    profile.powerLevels.indexOf(
      channel.power as FT60Profile['powerLevels'][number],
    ),
  )
  raw[8] = (raw[8] & 0x3f) | ((powerIndex & 0x03) << 6)
  const skipOffset = SKIP_OFFSET + ((channel.number - 1) >> 2)
  const skipShift = ((channel.number - 1) & 3) * 2
  const oldSkip = (image[skipOffset] >> skipShift) & 0x03
  const nextSkip = channel.skip ? oldSkip || 1 : 0
  image[skipOffset] =
    (image[skipOffset] & ~(0x03 << skipShift)) | (nextSkip << skipShift)
  encodeName(image, profile, channel.number, channel.name)
  updateFT60Checksum(image)
}

async function echoWrite(
  transport: RadioTransport,
  data: Uint8Array,
  timeoutMs = 2500,
): Promise<void> {
  await transport.write(data)
  const echo = await transport.readExact(data.byteLength, timeoutMs)
  if (compareImages(data, echo) !== null) {
    throw new Error('The FT-60 cable returned a corrupted command echo.')
  }
}

async function readAck(transport: RadioTransport, label: string): Promise<void> {
  const response = await transport.readExact(1, 2500)
  if (response[0] !== ACK) {
    throw new Error(`The FT-60 did not acknowledge ${label}.`)
  }
}

export async function readFT60RadioWithTransport(
  profile: FT60Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await transport.discardInput()
  const identity = await transport.readExact(8, 6000)
  const reportedModel = new TextDecoder('ascii')
    .decode(identity)
    .replace(/[^\x20-\x7e]/g, '')
    .trim()
  if (!new TextDecoder('ascii').decode(identity.slice(0, IDENTITY.length)).startsWith(IDENTITY)) {
    throw new Error(
      'The connected radio did not identify as a Yaesu FT-60 (expected AH017).',
    )
  }

  const image = new Uint8Array(profile.memorySize)
  image.set(identity)
  await echoWrite(transport, Uint8Array.of(ACK))

  let imageOffset = identity.byteLength
  for (let block = 0; block < BLOCK_COUNT; block += 1) {
    const size = block === BLOCK_COUNT - 1 ? 1 : BLOCK_SIZE
    const payload = await transport.readExact(size, 3500)
    image.set(payload, imageOffset)
    imageOffset += payload.byteLength
    await echoWrite(transport, Uint8Array.of(ACK))
    onProgress?.({
      current: block + 1,
      total: BLOCK_COUNT,
      label: 'Reading Yaesu FT-60 memory',
    })
  }
  if (imageOffset !== profile.memorySize) {
    throw new Error(
      `The FT-60 download was ${imageOffset} bytes; ${profile.memorySize} were expected.`,
    )
  }
  return { image, reportedModel: reportedModel || profile.model }
}

export async function writeFT60RadioWithTransport(
  profile: FT60Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < profile.memorySize) {
    throw new Error('The selected image is smaller than the FT-60 memory map.')
  }
  updateFT60Checksum(image)
  await echoWrite(transport, image.slice(0, 8))
  await readAck(transport, 'the cloning header')

  for (let block = 0; block < BLOCK_COUNT; block += 1) {
    const offset = 8 + block * BLOCK_SIZE
    const payload = image.slice(offset, Math.min(offset + BLOCK_SIZE, profile.memorySize))
    await echoWrite(transport, payload)
    await readAck(transport, `write block ${block}`)
    onProgress?.({
      current: block + 1,
      total: BLOCK_COUNT,
      label: 'Writing Yaesu FT-60 memory',
    })
  }
}
